import { type PropsWithChildren, type ReactNode, useState } from 'react';
import { capitalize } from 'es-toolkit';
import { motion, useReducedMotion } from 'framer-motion';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { logger } from '@zep/core-logger';
import { useOpenWindow } from '@/hooks';
import { BoltSmallRectangleColorMintIcon, BoltSmallRectangleColorVioletIcon } from '@/icons';
import { useUser } from '@/lib/auth';
import { usePrices } from '@/module/payment/hooks/usePrices';
import { useCountryCode } from '@/shared/hooks';
import { formatCurrencyPrice } from '@/shared/utils/currency';
import { type PlanType } from '@/types';
import { Button } from '@/ui_v3';
import { cn, combinePathAndQuery } from '@/utils';
import { usePaymentFlow } from '../../shared';
import { getPaymentLoginUrl } from '../../shared/utils/paymentEntryUrl';
import { PRICING_TAB_QUERY_KEY } from '../PricingSection1/pricingTabQuery';

// 단체(학교) Pro 월 가격. 전용 가격 API 가 없어 하드코딩(ProSchoolPlan 과 동일 값).
const GROUP_PRO_MONTHLY_PRICE = 30000;
const AUDIENCE_SEGMENT_LAYOUT_ID = 'pricing-audience-segment-active-surface';
const AUDIENCE_SEGMENT_TRANSITION = {
  duration: 0.22,
  ease: [0.16, 1, 0.3, 1]
} as const;
type Audience = 'individual' | 'group';
export const TableHeader = () => {
  const {
    t
  } = useTranslation(['common']);
  const {
    countryCode,
    isKR
  } = useCountryCode();
  const basicPrices = usePrices({
    countryCode,
    planType: 'BASIC'
  });
  const basicPriceText = formatCurrencyPrice(basicPrices.displayFinalPrice, basicPrices.displayCurrency);
  const proPrices = usePrices({
    countryCode,
    planType: 'PRO'
  });
  const proPriceText = formatCurrencyPrice(proPrices.displayFinalPrice, proPrices.displayCurrency);
  const groupProPriceText = formatCurrencyPrice(GROUP_PRO_MONTHLY_PRICE, proPrices.displayCurrency);

  // Pro 토글이 열 높이를 늘리므로 Free/Basic 엔 동일 높이 placeholder 로 라인 정렬.
  const openWindow = useOpenWindow();
  const [audience, setAudience] = useState<Audience>('group');
  const proPriceForAudience = audience === 'group' ? groupProPriceText : proPriceText;
  const isProGroupInquiry = isKR && audience === 'group';
  return <div className="grid grid-cols-3 gap-x-lg laptop-sm:grid-cols-4" data-sentry-component="TableHeader" data-sentry-source-file="TableHeader.tsx">
      <div className="hidden min-w-[160px] laptop-sm:block" />
      <PlanTypeColumn planType="FREE" priceText={t('common:pricing.free.free')} ctaText={t('common:pricing.free.startForFree')} toggleSlot={isKR ? <AudienceSegmentBarPlaceholder /> : undefined} data-sentry-element="PlanTypeColumn" data-sentry-source-file="TableHeader.tsx" />
      <PlanTypeColumn planType="BASIC" priceText={basicPriceText} ctaText={t('common:pricing.purchaseSelectedPlan')} toggleSlot={isKR ? <AudienceSegmentBarPlaceholder /> : undefined} data-sentry-element="PlanTypeColumn" data-sentry-source-file="TableHeader.tsx" />
      <PlanTypeColumn planType="PRO" priceText={isKR ? proPriceForAudience : proPriceText} ctaText={isProGroupInquiry ? t('common:pricing.schoolGroup.inquiry') : t('common:pricing.purchaseSelectedPlan')} toggleSlot={isKR ? <AudienceSegmentBar audience={audience} onChange={setAudience} /> : undefined} onCtaClick={isProGroupInquiry ? () => {
      logger.track('click_get_quote_button');
      openWindow(`/pricing/quote?${PRICING_TAB_QUERY_KEY}=group`);
    } : undefined} data-sentry-element="PlanTypeColumn" data-sentry-source-file="TableHeader.tsx" />
    </div>;
};
interface PlanTypeColumnProps {
  planType: PlanType;
  priceText: string;
  ctaText: string;
  /** 가격 위 토글 슬롯. Pro=개인/단체 토글, Free/Basic=정렬용 placeholder. */
  toggleSlot?: ReactNode;
  /** CTA 클릭 동작 override(예: 단체 Pro=견적 문의). 없으면 기본 플랜별 동작. */
  onCtaClick?: () => void;
}
const PlanTypeColumn = ({
  planType,
  priceText,
  ctaText,
  toggleSlot,
  onCtaClick
}: PlanTypeColumnProps) => {
  const {
    t
  } = useTranslation(['common']);
  const router = useRouter();
  const {
    isAuthenticated
  } = useUser();
  const {
    countryCode
  } = useCountryCode();
  const openWindow = useOpenWindow();
  const {
    startPaymentFlow
  } = usePaymentFlow();
  const isPaid = planType !== 'FREE';
  return <div className="flex min-w-[160px] flex-col items-start gap-xs px-xs py-md" data-sentry-component="PlanTypeColumn" data-sentry-source-file="TableHeader.tsx">
      <div className="flex items-center gap-2xs">
        {planType === 'BASIC' && <BoltSmallRectangleColorMintIcon size="md" />}
        {planType === 'PRO' && <BoltSmallRectangleColorVioletIcon size="md" />}
        <strong className="text-heading-xl-extrabold">
          {capitalize(planType)}
        </strong>
      </div>

      {toggleSlot}

      <div className="flex items-center gap-3xs pb-sm">
        <span className="text-body-lg-semibold">{priceText}</span>
        {isPaid && <span className="text-body-sm-medium text-alternative">
            {`/${t('common:pricing.perMonths', {
          n: 1
        })}`}
          </span>}
      </div>

      <Button size="md" variant={isPaid ? 'contained' : 'outlined'} intent={(() => {
      switch (planType) {
        case 'FREE':
          return 'neutral';
        case 'BASIC':
          return 'alternative';
        case 'PRO':
          return 'gradient';
      }
    })()} onClick={() => {
      if (onCtaClick) {
        onCtaClick();
        return;
      }
      logger.track('click_pricing_table_cta', {
        planType
      });
      if (planType === 'FREE') {
        void router.push(isAuthenticated ? '/build' : combinePathAndQuery('/login', router.query, [PRICING_TAB_QUERY_KEY]));
      } else {
        if (!isAuthenticated) {
          openWindow(getPaymentLoginUrl(planType, countryCode));
          return;
        }
        startPaymentFlow({
          plan: planType,
          countryCode
        }, {
          target: 'new-tab'
        });
      }
    }} data-sentry-element="Button" data-sentry-source-file="TableHeader.tsx">
        {ctaText}
      </Button>
    </div>;
};
const AudienceSegmentBar = ({
  audience,
  onChange
}: {
  audience: Audience;
  onChange: (audience: Audience) => void;
}) => {
  const {
    t
  } = useTranslation(['common']);
  return <div role="group" aria-label={t('common:pricing.schoolGroup.audienceLabel')} className="flex h-9 w-[94px] items-center rounded-full bg-neutral shadow-[inset_0_0_0_1px_rgba(28,27,31,0.04)]" data-sentry-component="AudienceSegmentBar" data-sentry-source-file="TableHeader.tsx">
      <AudienceSegment active={audience === 'individual'} onClick={() => onChange('individual')} data-sentry-element="AudienceSegment" data-sentry-source-file="TableHeader.tsx">
        {t('common:pricing.schoolGroup.individual')}
      </AudienceSegment>
      <AudienceSegment active={audience === 'group'} onClick={() => onChange('group')} data-sentry-element="AudienceSegment" data-sentry-source-file="TableHeader.tsx">
        {t('common:pricing.schoolGroup.group')}
      </AudienceSegment>
    </div>;
};

// Free/Basic 정렬용 placeholder. 실제 토글과 같은 높이만 차지하고 포커스 대상은 만들지 않는다.
const AudienceSegmentBarPlaceholder = () => <div className="h-9" aria-hidden data-sentry-component="AudienceSegmentBarPlaceholder" data-sentry-source-file="TableHeader.tsx" />;
const AudienceSegment = ({
  active,
  onClick,
  children
}: PropsWithChildren<{
  active: boolean;
  onClick: () => void;
}>) => {
  const shouldReduceMotion = useReducedMotion();
  return <button type="button" aria-pressed={active} onClick={onClick} className={cn('relative flex h-9 min-w-[47px] flex-1 items-center justify-center rounded-full px-sm text-body-sm-semibold transition-colors', 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-strong focus-visible:ring-offset-1', active ? 'text-default' : 'text-assistive hover:text-neutral')} data-sentry-component="AudienceSegment" data-sentry-source-file="TableHeader.tsx">
      {active && <motion.span layoutId={AUDIENCE_SEGMENT_LAYOUT_ID} className="absolute inset-0 rounded-full bg-default shadow-[inset_0_0_0_1px_rgba(28,27,31,0.04),0_0_10px_0_rgba(42,39,65,0.08),0_3px_6px_0_rgba(42,39,65,0.08)]" transition={shouldReduceMotion ? {
      duration: 0
    } : AUDIENCE_SEGMENT_TRANSITION} />}
      <span className="relative">{children}</span>
    </button>;
};